import { useToast } from "native-base";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { validationCheck } from "utils/validation";
import { getStore } from "store/storeUtils";
import { CREATE_MODALS } from "global/constants";
import dayjs from "dayjs";
import { apiSingleCreate } from "api/apiCall";
import { useMutation } from "@tanstack/react-query";
import AmplitudeHelper from "utils/analytics";
import { getBanksList, getCategoriesList, getGroupsAndPayDatesList } from "api/useQueryCalls";
import { fieldNames } from "global/fieldNameMapping";
import { useTranslation } from "react-i18next";
import { checkForActiveHandler, showErrorToast } from "utils/utilities";
import useEmployeeDetails from "utils/employee/useEmployeeDetails";

const useConnect = () => {
  const { t } = useTranslation("common");
  const toast = useToast();
  const navigate = useNavigate();
  const { modal, setModal, resetLoginData } = getStore();

  const [formData, setFormData] = useState({
    suspensionCategory: "",
    suspendedReason: "",
  });
  const [errors, setErrors] = useState({});

  const { groupPayDates, handleGroupPayDates, handleBanksListError, showCreateUpdateSuccessToast } =
    useEmployeeDetails();

  const handleSubmit = (values) => {
    const newErrors = {};
    const required = [
      "name",
      "phone",
      // "birthDate", removed from Mandatory fields discussed with Desy and team
      "ktp",
      "employeeId",
      // "group", removed from Mandatory fields discussed with Desy and team
      "bankNumber",
      "bankName",
      "beneficiaryName",
      "employeeType",
      "status",
      "salary",
      "payDate",
      "gender",
      "dateJoined",
    ];
    const inputs = [
      // "name",
      "phone",
      // "ktp",
      "birthDate",
      "dateJoined",
      // 'employeeId',
      // "bankNumber",
      // "beneficiaryName",
      // "salary",
    ];

    required.forEach((field) => {
      const fieldValue = values[field];
      if (!fieldValue || fieldValue.toString().trim() == "") {
        newErrors[`${field}`] = "basicValidation.missingFields";
      }
    });

    inputs.forEach((field) => {
      let validation = "";
      if (field === "birthDate" || field === "dateJoined") {
        validation = validationCheck[`${field}`](values[field]);
      } else {
        validation = validationCheck[`${field}`](values[`${field}`]);
      }
      if (validation != "" && newErrors[`${field}`] != "basicValidation.missingFields") {
        newErrors[`${field}`] = validation;
      }
    });

    if (Object.keys(newErrors).length != 0) {
      setErrors(newErrors);
    } else {
      setErrors({});
      setFormData(values);
      AmplitudeHelper.logEvent("save_button_clicked", {
        source: "employee_single_creation_page",
      });

      setModal({
        ...modal,
        type: CREATE_MODALS.singleCreate,
        data: { ...values },
      });
    }
  };

  const handleCancel = () => {
    AmplitudeHelper.logEvent("cancel_button_clicked", {
      source: "employee_single_creation_page",
    });
    setModal({ ...modal, type: CREATE_MODALS.singleCreateCancel });
  };

  const {
    isSuccess: createSuccess,
    isError: isCreateError,
    error: createError,
    mutate: createEmployee,
    data: createEmployeeResponse,
  } = useMutation({ mutationFn: (data) => apiSingleCreate(data) });

  const { isError: banksListIsError, error: banksListError, data: banksListData = [] } = getBanksList();

  const { data: categoriesData = [] } = getCategoriesList();

  const { data: groupsPayDatesData = [], isSuccess: getGroupsPayDatesIsSuccess } = getGroupsAndPayDatesList();

  useEffect(() => {
    handleGroupPayDates({
      getGroupsPayDatesIsSuccess,
      groupsPayDatesData,
    });
  }, [getGroupsPayDatesIsSuccess]);

  useEffect(() => {
    handleBanksListError({
      banksListIsError,
      banksListError,
      toast,
      navigate,
      resetLoginData,
    });
  }, [banksListIsError, banksListError]);

  useEffect(() => {
    if (isCreateError) {
      const newErrors = {};

      createError.forEach((errorCode) => {
        const field = fieldNames[errorCode]; // map field name to particular error code and select out that one field which corresponds to the error code
        if (field) {
          newErrors[`${field}`] = t(`errorCodes.${errorCode}`);
        }
      });

      if (Object.keys(newErrors).length != 0) {
        setErrors(newErrors);
      } else {
        showErrorToast({ error: createError, toast, navigate, resetLoginData });
      }
    }
  }, [isCreateError, createError]);

  useEffect(() => {
    if (createSuccess) {
      showCreateUpdateSuccessToast({
        createEmployeeResponse,
        navigate,
        toast,
        isCreate: true,
      });
    }
  }, [createSuccess]);

  const mutateData = (data) => {
    const nameParts = data?.name.trim().split(" ");

    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");
    const updatedValues = {
      requestType: "create_employee",
      firstName,
      lastName,
      birthDate: data?.birthDate ? dayjs(data.birthDate).format("YYYY-MM-DD") : undefined,
      mobileNumber: data.phone,
      gender: data.gender,
      ktpNumber: data.ktp,
      employeeCode: data.employeeId,
      joinedAt: dayjs(data.dateJoined).format("YYYY-MM-DD"),
      collarType: data.employeeType?.data,
      groupName: data?.group ? (data?.group === t("employeeCreation.single.noGroup") ? "" : data?.group) : "",
      salaryType: "FIXED_MONTH",
      employeeStatus: data?.status === "Aktif" || data?.status === "active" ? "active" : "withdraw_suspended",
      suspensionCategory: data.suspensionCategory !== "" ? data.suspensionCategory : null,
      suspendedReason:
        data.suspendedReason !== "" && data.suspensionCategory.includes("Other") ? data.suspendedReason : null,
      payDate: data?.payDate,
      salaryAmount: data.salary,
      bankName: data.bankName.bankName,
      bankId: data.bankName.id,
      bankAccountNo: data.bankNumber,
      beneficiaryName: data.beneficiaryName,
    };

    return checkForActiveHandler(data, updatedValues);
  };

  const handleSendData = () => {
    let sendData = mutateData(formData);
    createEmployee(sendData);
  };

  return {
    formData,
    banksList: banksListData?.list || [],
    handleSendData,
    handleSubmit,
    handleCancel,
    errors,
    categoriesData,
    groupPayDates,
  };
};

export default useConnect;
